import React, { useEffect, useState } from 'react';
import styles from '../../assets/styles/Setting.module.scss';
import AccountSettingsLayout from '../../components/Setting/AccountSettingsLayout';
import { ReactComponent as DownloadIcon } from '../../assets/icons/icon-download-file.svg';

/**
 * サブスクリプションステータス (StripeのSubscription Statusに準拠)
 * - active: 有効
 * - past_due: 支払い遅延
 * - canceled: 解約済み
 * - unpaid: 未払い
 * - incomplete: 不完全 (初回決済待ちなど)
 * - incomplete_expired: 不完全なまま期限切れ
 * - trialing: トライアル中
 * - unregistered: 未登録 (StripeにCustomerやSubscriptionが存在しない初期状態 - 独自ステータス)
 */
type PaymentStatusType = 'active' | 'past_due' | 'canceled' | 'unpaid' | 'incomplete' | 'incomplete_expired' | 'trialing' | 'unregistered';

/**
 * 請求書ステータス (StripeのInvoice Statusに準拠)
 * - paid: 決済完了 (正常にクレジットカードで支払いが完了した場合)
 * - open: 未決済 (請求書は発行されたが、まだ支払いが完了していない状態。カード決済失敗時などもこれになります)
 * - void: 無効 (Stripe側で請求書がキャンセル・無効化された場合)
 * - uncollectible: 回収不能 (長期間支払いがなく、Stripe側で回収を諦める設定になった場合)
 * - draft: 下書き (請求書が作成中・まだ確定していない状態)
 */
type InvoiceStatusType = 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';

interface PaymentStatus {
  status: PaymentStatusType;
  plan: string;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  default_card_brand: string | null;
  default_card_last4: string | null;
  default_card_exp_month: number | null;
  default_card_exp_year: number | null;
  recurring_amount: number | null;
  currency: string | null;
}

interface PaymentHistory {
  invoice_number: string;
  status: InvoiceStatusType;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  currency: string;
  created_at: string;
  paid_at: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf_url: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  active: '契約中',
  past_due: '支払い遅延',
  canceled: '解約済み',
  unpaid: '未払い',
  incomplete: '未完了',
  trialing: 'トライアル中',
  unregistered: '未登録',
  paid: '決済完了',
  open: '未決済',
  void: '無効',
  uncollectible: '回収不能',
  draft: '下書き'
};

const SettingPlan: React.FC = () => {
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [history, setHistory] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, historyRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/payments/status`, { credentials: 'include' }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/payments/history`, { credentials: 'include' })
        ]);
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setStatus(statusData);
        }
        
        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setHistory(historyData.history || []);
        }
      } catch (error) {
        console.error('Error fetching payment data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const formatCurrency = (amount: number | null) => {
    if (amount == null) return '¥0';
    return `¥${amount.toLocaleString()}`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const getNextMonthDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
  };

  const getPreviousMonthString = (dateStr: string | null) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const m = d.getMonth() === 0 ? 12 : d.getMonth();
    return `${m}月分`;
  };


  const handleUpdatePayment = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          success_url: window.location.href,
          cancel_url: window.location.href
        })
      });
      if (!res.ok) throw new Error('API error');
      const data = await res.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('決済画面への遷移に失敗しました。');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('本当に解約しますか？次回更新日で自動課金が停止し、Freeプランに戻ります。')) {
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stripe/cancel-subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'API error');
      }
      alert('解約手続きが完了しました。');
      window.location.reload();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      alert('解約に失敗しました。');
    }
  };

  const handleDownload = (url: string | null) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      alert('領収書がまだ発行されていません。');
    }
  };

  const handleResetToFree = async () => {
    if (!window.confirm('[DEBUG] 本当にFreeプランに強制リセットしますか？\n※テスト用の機能です。')) {
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stripe/debug/reset-to-free`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('API error');
      alert('Freeプランにリセットしました。');
      window.location.reload();
    } catch (error) {
      console.error('Error resetting to free:', error);
      alert('リセットに失敗しました。');
    }
  };

  const handleSimulateFailure = async () => {
    if (!window.confirm('[DEBUG] 決済失敗（未決済）状態をシミュレートしますか？\n※現在Standardプランの場合のみ有効です。')) {
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/stripe/debug/simulate-failure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('API error');
      alert('決済失敗（未決済）状態を生成しました！');
      window.location.reload();
    } catch (error) {
      console.error('Error simulating failure:', error);
      alert('シミュレートに失敗しました。');
    }
  };

  const PLAN = {
    TRIAL: 'trial',
    FREE: 'free',
    STANDARD: 'standard',
  } as const;

  const plan = status?.plan ?? PLAN.TRIAL;

  const isTrial = plan === PLAN.TRIAL;
  const isFree = plan === PLAN.FREE;
  const isStandard = plan === PLAN.STANDARD;
  const baseAmount = isStandard ? (status?.recurring_amount ?? 39800) : null;

  return (
    <AccountSettingsLayout>
      <div className={styles.twoColumnGrid}>

        {/* Left Column: Billing Status & History (Wider) */}
        <div className={styles.leftColumnWide}>
          {/* Next Month Billing */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3 className={styles.planHeaderFlex}>
                次回の請求予定金額
                {isStandard && status?.current_period_end && (
                  <span className={styles.planHeaderDate}>
                    {formatDate(status.current_period_end)} 請求予定
                  </span>
                )}
              </h3>
            </div>
            <div className={styles.settingDetailContentWrap}>
              <div className={styles.billingRow}>
                <span className={styles.label}>
                  基本料金
                  {isStandard && status?.current_period_end && (
                    <span className={styles.planSubtext}>
                      （{formatDate(status.current_period_end)} 〜 {getNextMonthDate(status.current_period_end)}）
                    </span>
                  )}
                </span>
                <span className={styles.value}>{formatCurrency(baseAmount)}</span>
              </div>
              <div className={`${styles.billingRow} last`}>
                <span className={styles.label}>
                  運用手数料 (5%)
                  {isStandard && status?.current_period_end && (
                    <span className={styles.planSubtext}>
                      （{getPreviousMonthString(status.current_period_end)}：消化金額確定後 × 5%）
                    </span>
                  )}
                </span>
                <span className={styles.value}>
                  {isStandard ? '未確定 (¥0)' : '¥0'}
                </span>
              </div>

              {/* Free/Trial Message */}
              {isTrial && (
                <div className={styles.freeMessage}>
                  現在Trialプラン（テスト期間）につき無料でご利用いただけます。
                </div>
              )}
              {isFree && (
                <div className={styles.freeMessage}>
                  現在Freeプランです。一部機能が制限されています。
                </div>
              )}

              <div className={styles.billingTotal}>
                <div className={styles.amount}>
                  {isStandard ? <span className={styles.amountUnconfirmed}>未確定</span> : formatCurrency(null)}
                </div>
              </div>
            </div>
          </div>

          {/* Billing History */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>請求履歴</h3>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.historyTable}>
                <thead>
                  <tr>
                    <th>日付</th>
                    <th>基本料金</th>
                    <th>手数料</th>
                    <th>消費税</th>
                    <th>合計</th>
                    <th>ステータス</th>
                    <th className={styles.textCenter}>領収書</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && history.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={styles.textCenter}>履歴がありません</td>
                    </tr>
                  ) : (
                    history.map((item, idx) => {
                      const isPending = item.status === 'open' || item.status === 'draft';
                      return (
                        <tr key={idx}>
                          <td>{formatDate(item.created_at)}</td>
                          <td>{formatCurrency(item.subtotal)}</td>
                          <td>¥0</td>
                          <td>{formatCurrency(item.tax_amount)}</td>
                          <td className={styles.textBold}>{formatCurrency(item.total_amount)}</td>
                          <td>
                            <span className={`${styles.statusBadge} ${isPending ? styles.statusPending : styles.statusActive}`}>
                              {STATUS_LABELS[item.status] || item.status}
                            </span>
                          </td>
                          <td className={styles.textCenter}>
                            <button 
                              className={styles.iconButton} 
                              title="ダウンロード"
                              onClick={() => handleDownload(item.invoice_pdf_url || item.hosted_invoice_url)}
                            >
                              <DownloadIcon width={16} height={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>

        {/* Right Column: Current Plan & Payment Method (Slightly narrower) */}
        <div className={`${styles.rightColumn} ${styles.rightColumnFixed}`}>
          {/* Current Plan */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>現在のプラン</h3>
            </div>
            <div className={styles.planDetail}>
              <div className={styles.planName}>
                {{
                  [PLAN.STANDARD]: 'STANDARD PLAN',
                  [PLAN.FREE]: 'FREE PLAN',
                  [PLAN.TRIAL]: 'TRIAL PLAN'
                }[plan] || 'TRIAL PLAN'}
              </div>
              <div className={styles.planPrice}>
                {isStandard ? formatCurrency(baseAmount) : '¥0'}<span>/ month</span>
              </div>
              <div className={styles.planNote}>
                {isStandard 
                  ? '+ 広告運用費の5%' 
                  : (
                    <>
                      Standardプランにアップグレードすると、<br />
                      すべての機能が無制限でご利用いただけます。
                    </>
                  )}
              </div>

              {isStandard && (
                <div className={styles.renewalBadge}>
                  {status?.cancel_at_period_end 
                    ? `解約予約済み (${formatDate(status?.current_period_end || null)}で終了)`
                    : `次回更新日: ${formatDate(status?.current_period_end || null)}`}
                </div>
              )}

              <div className={styles.buttonCenterWrapper}>
                {(isFree || isTrial) ? (
                  <button className={styles.planButton} onClick={handleUpdatePayment}>
                    Standardプランにアップグレード
                  </button>
                ) : (
                  <span onClick={handleCancelSubscription} className={styles.cancelLink}>解約はこちら</span>
                )}
              </div>

              {/* DEBUG RESET BUTTON */}
              {process.env.NODE_ENV === 'development' && (
                <div className={styles.debugActionContainer}>
                  <button 
                    onClick={handleResetToFree} 
                    className={styles.debugButtonReset}
                  >
                    [DEBUG]<br />Freeプランリセット
                  </button>
                  <button 
                    onClick={handleSimulateFailure} 
                    className={styles.debugButtonSimulate}
                  >
                    [DEBUG]<br />未決済をシミュレート
                  </button>
                </div>
              )}

            </div>
          </div>

          {/* Credit Card Info */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h3>お支払い方法</h3>
              {isStandard && !status?.cancel_at_period_end && (
                <button className={styles.actionButton} onClick={handleUpdatePayment}>
                  {status?.default_card_last4 ? '変更' : '登録'}
                </button>
              )}
            </div>
            <div className={styles.cardInfo}>
              {status?.default_card_last4 ? (
                <>
                  <div className={styles.cardIcon}>
                    {status.default_card_brand ? status.default_card_brand.toUpperCase() : 'CARD'}
                  </div>
                  <div>
                    <div className={styles.cardNumber}>**** **** **** {status.default_card_last4}</div>
                    <div className={styles.cardExpiry}>
                      有効期限: {String(status.default_card_exp_month).padStart(2, '0')}/{String(status.default_card_exp_year).slice(-2)}
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.freeMessage}>カード情報が登録されていません</div>
              )}
            </div>
          </div>
        </div>

      </div>
    </AccountSettingsLayout>
  );
};

export default SettingPlan;
